const TOKEN_KEY = 'gm_github_token';
const USER_KEY = 'gm_github_user';

export interface GitHubUser {
  id: number;
  login: string;
  name: string | null;
  email: string | null;
  avatar_url: string;
  html_url: string;
}

export const githubAuth = {
  /** Redirect the browser to GitHub's OAuth authorize page */
  login() {
    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
    if (!clientId) {
      alert('GitHub OAuth is not configured.\nAdd REACT_APP_GITHUB_CLIENT_ID to your .env file.');
      return;
    }
    const redirectUri = `${window.location.origin}/auth/callback`;
    // user:email is required to read the user's email addresses via /user/emails
    const scope = 'repo read:user user:email';
    window.location.href =
      `https://github.com/login/oauth/authorize` +
      `?client_id=${clientId}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}`;
  },

  /** Exchange the OAuth code for an access token via the serverless function */
  async exchangeCode(code: string): Promise<string> {
    const res = await fetch(`/api/github-token?code=${encodeURIComponent(code)}`);
    const data = await res.json();
    if (data.error) {
      throw new Error(data.error_description || data.error);
    }
    return data.access_token as string;
  },

  /** Fetch the authenticated GitHub user's profile */
  async fetchUser(token: string): Promise<GitHubUser> {
    const res = await fetch('https://api.github.com/user', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch GitHub user');
    return res.json();
  },

  /**
   * Record this sign-in on the server (Supabase) so we know who uses GitHubMate.
   * The serverless function re-verifies the token with GitHub and resolves the
   * user's primary verified email. Best-effort: failure never blocks sign-in.
   */
  async trackUser(token: string): Promise<void> {
    try {
      await fetch('/api/track-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    } catch {
      /* tracking is non-critical — ignore network errors */
    }
  },

  saveToken(token: string) { localStorage.setItem(TOKEN_KEY, token); },
  getToken(): string | null { return localStorage.getItem(TOKEN_KEY); },

  saveUser(user: GitHubUser) { localStorage.setItem(USER_KEY, JSON.stringify(user)); },
  getUser(): GitHubUser | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  },

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  isLoggedIn(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  },
};
